
import { useSiteAnalytics } from '@/hooks/useCMS'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts'
import { useState } from 'react'
import { TrendingUp, Users, Eye, MousePointer, Smartphone, Monitor, Tablet } from 'lucide-react'

export const CMSAnalytics = () => {
  const [timeRange, setTimeRange] = useState('7d')
  const { data: analytics, isLoading } = useSiteAnalytics(timeRange)

  // Traitement des données pour les graphiques
  const processAnalyticsData = () => {
    if (!analytics) return { pageViews: [], deviceStats: [], dailyStats: [] }

    // Statistiques par page
    const pageViews = analytics.reduce((acc: any[], visit) => {
      const existing = acc.find(item => item.page === visit.page_slug)
      if (existing) {
        existing.views += 1
      } else {
        acc.push({ page: visit.page_slug || '/', views: 1 })
      }
      return acc
    }, [])

    // Statistiques par appareil
    const deviceStats = analytics.reduce((acc: any[], visit) => {
      const device = visit.device_type || 'unknown'
      const existing = acc.find(item => item.device === device)
      if (existing) {
        existing.count += 1
      } else {
        acc.push({ device, count: 1 })
      }
      return acc
    }, [])

    // Statistiques quotidiennes
    const dailyStats = analytics.reduce((acc: any[], visit) => {
      const date = new Date(visit.visited_at).toLocaleDateString('fr-FR')
      const existing = acc.find(item => item.date === date)
      if (existing) {
        existing.visits += 1
      } else {
        acc.push({ date, visits: 1 })
      }
      return acc
    }, []).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    return { pageViews, deviceStats, dailyStats }
  }

  const { pageViews, deviceStats, dailyStats } = processAnalyticsData()

  const totalVisits = analytics?.length || 0
  const uniqueVisitors = new Set(analytics?.map(v => v.visitor_id)).size
  const mobileVisits = analytics?.filter(v => v.device_type === 'mobile').length || 0
  const bounceRate = Math.round((mobileVisits / totalVisits) * 100) || 0

  if (isLoading) {
    return <div>Chargement des analytics...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Analytics du site</h2>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 jours</SelectItem>
            <SelectItem value="30d">30 jours</SelectItem>
            <SelectItem value="all">Tout</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Visites totales</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalVisits}</div>
            <p className="text-xs text-muted-foreground">
              +12% par rapport à la période précédente
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Visiteurs uniques</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{uniqueVisitors}</div>
            <p className="text-xs text-muted-foreground">
              +8% par rapport à la période précédente
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taux de rebond</CardTitle>
            <MousePointer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{bounceRate}%</div>
            <p className="text-xs text-muted-foreground">
              -3% par rapport à la période précédente
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mobile</CardTitle>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round((mobileVisits / totalVisits) * 100)}%
            </div>
            <p className="text-xs text-muted-foreground">
              des visites depuis mobile
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Visites quotidiennes</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="visits" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pages les plus visitées</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pageViews.slice(0, 5)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="page" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="views" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tableau détaillé */}
      <Card>
        <CardHeader>
          <CardTitle>Détail des pages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {pageViews.map((page, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">{page.page}</p>
                  <p className="text-sm text-gray-500">{page.views} visites</p>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-500">+5%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Statistiques par appareil */}
      <Card>
        <CardHeader>
          <CardTitle>Répartition par appareil</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {deviceStats.map((device, index) => {
              const percentage = Math.round((device.count / totalVisits) * 100)
              const Icon = device.device === 'mobile' ? Smartphone : 
                          device.device === 'tablet' ? Tablet : Monitor
              
              return (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium capitalize">{device.device}</p>
                      <p className="text-sm text-gray-500">{device.count} visites</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">{percentage}%</p>
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
